import {
  useLocation,
  useNavigate,
  useParams,
  useMatch
} from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  IconButton,
  Typography,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { WorkOrdersList, EquipmentList, SidebarSection } from './components';
import IndySoftLogo from '../../assets/AppLogo.png';

type Props = {
  open: boolean;
  onClose: () => void;
  drawerWidth: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

type SidebarItem = {
  text: string;
  id: string;
  isNavigation?: boolean;
  path?: string;
};

type SidebarContent = {
  title: string;
  items: SidebarItem[];
};

type RouteParams = {
  jobNumber?: string;
  woDetailGlobalId?: string;
};

const Sidebar = ({
  open,
  onClose,
  drawerWidth,
  collapsed = true,
  onToggleCollapse
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('sidebar');

  // Use React Router's hooks to detect current route
  const params = useParams<RouteParams>();
  const isCalibrationPage = useMatch(
    '/work/:jobNumber/:woDetailGlobalId/calibration'
  );
  const isDetailPage = useMatch('/work/:jobNumber/:woDetailGlobalId');
  const isWorkOrderPage = useMatch('/work/:jobNumber');
  const isHistoryPage = location.pathname.toLowerCase().startsWith('/history');
  const isScriptProfilePage = useMatch('/scripts/:scriptId');
  const isScriptsPage = location.pathname.startsWith('/scripts');
  const isSettingsPage = location.pathname === '/settings';

  // Build page context from route matches
  const pageContext = {
    isCalibrationPage: !!isCalibrationPage,
    isDetailPage: !!isDetailPage && !isCalibrationPage,
    isWorkOrderPage: !!isWorkOrderPage && !isDetailPage && !isCalibrationPage,
    jobNumber: params.jobNumber,
    woDetailGlobalId: params.woDetailGlobalId
  };

  // Determine sidebar content based on current route
  const getSidebarContent = (): SidebarContent | null => {
    if (isCalibrationPage && params.jobNumber && params.woDetailGlobalId) {
      return {
        title: 'Calibration',
        items: [
          {
            text: 'Back to Detail',
            id: 'backToWorkOrderDetail',
            isNavigation: true,
            path: `/work/${params.jobNumber}/${params.woDetailGlobalId}`
          }
        ]
      };
    }

    if (isDetailPage && params.jobNumber) {
      return {
        title: 'Calibration Runtime',
        items: [
          {
            text: 'Back to Work Order',
            id: 'backToWorkOrder',
            isNavigation: true,
            path: `/work/${params.jobNumber}`
          }
        ]
      };
    }

    if (isWorkOrderPage && params.jobNumber) {
      return {
        title: 'Work Order',
        items: [
          {
            text: 'Back to Work Orders',
            id: 'backToWorkOrders',
            isNavigation: true,
            path: '/work'
          }
        ]
      };
    }

    if (isScriptProfilePage) {
      return {
        title: 'Script',
        items: [
          {
            text: 'Back to Scripts',
            id: 'backToScripts',
            isNavigation: true,
            path: '/scripts'
          }
        ]
      };
    }

    if (isScriptsPage) {
      return {
        title: 'Scripts',
        items: [
          {
            text: 'Back to Work Orders',
            id: 'backToWorkOrders',
            isNavigation: true,
            path: '/work'
          }
        ]
      };
    }

    if (isHistoryPage) {
      return {
        title: t('history.title'),
        items: [
          {
            text: t('history.home'),
            id: 'home',
            isNavigation: true,
            path: '/work'
          }
        ]
      };
    }

    if (isSettingsPage) {
      return {
        title: 'User Settings',
        items: [
          { text: 'Basic info', id: 'basic-info' },
          { text: 'Emails', id: 'emails' },
          { text: 'Organizations', id: 'organizations' }
        ]
      };
    }

    return null;
  };

  const sidebarContent = getSidebarContent();
  const effectiveWidth = collapsed ? 60 : drawerWidth;

  const handleItemClick = (item: SidebarItem) => {
    if (item.isNavigation && item.path) {
      navigate(item.path);
    }
  };

  const handleWorkOrderNavigate = (jobNumber: string) => {
    navigate(`/work/${jobNumber}`);
  };

  const handleEquipmentNavigate = (woDetailGlobalId: string) => {
    if (pageContext.jobNumber) {
      navigate(
        `/work/${pageContext.jobNumber}/calibration?instrument=${encodeURIComponent(
          woDetailGlobalId
        )}`
      );
    }
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Toolbar />
      {sidebarContent && (
        <>
          {/* Header with title and collapse toggle */}
          <Box
            sx={{
              px: collapsed ? 1 : 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            {!collapsed && sidebarContent.title && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: '14px',
                  letterSpacing: '0.1px'
                }}
              >
                {sidebarContent.title}
              </Typography>
            )}
            {onToggleCollapse && (
              <IconButton
                onClick={onToggleCollapse}
                size="small"
                sx={{
                  ml: collapsed ? 0 : 'auto',
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            )}
          </Box>
          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {collapsed && sidebarContent.items.some(i => i.isNavigation) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const navItem = sidebarContent.items.find(
                      i => i.isNavigation
                    );
                    if (navItem?.path) navigate(navItem.path);
                  }}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {!collapsed && (
              <List>
                {sidebarContent.items.map(item => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleItemClick(item)}
                      sx={{
                        px: 2,
                        justifyContent: 'flex-start',
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        },
                        '&.Mui-selected': {
                          backgroundColor:
                            theme.palette.mode === 'light'
                              ? 'rgba(1, 103, 124, 0.08)'
                              : 'rgba(68, 156, 180, 0.16)',
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'light'
                                ? 'rgba(1, 103, 124, 0.12)'
                                : 'rgba(68, 156, 180, 0.24)'
                          }
                        }
                      }}
                    >
                      {item.isNavigation && (
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: 'inherit',
                            justifyContent: 'center'
                          }}
                        >
                          {item.id === 'home' ? (
                            <HomeIcon fontSize="small" />
                          ) : (
                            <ArrowBackIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '14px',
                          fontWeight: 400,
                          letterSpacing: '0.15px'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {/* Equipment List - Show only on Detail Page */}
            {pageContext.isDetailPage && pageContext.jobNumber && (
              <SidebarSection
                title={t('sections.scales')}
                collapsed={collapsed}
              >
                <EquipmentList
                  jobNumber={pageContext.jobNumber}
                  currentWoDetailGlobalId={pageContext.woDetailGlobalId}
                  collapsed={collapsed}
                  onNavigate={handleEquipmentNavigate}
                />
              </SidebarSection>
            )}

            {/* Work Orders List - Show on all work-related pages */}
            {(pageContext.isWorkOrderPage || pageContext.isDetailPage) && (
              <SidebarSection
                title={t('sections.workOrders')}
                collapsed={collapsed}
              >
                <WorkOrdersList
                  currentJobNumber={pageContext.jobNumber}
                  collapsed={collapsed}
                  onNavigate={handleWorkOrderNavigate}
                />
              </SidebarSection>
            )}
          </Box>
          {/* Powered by IndySoft */}
          {!collapsed && (
            <Box
              sx={{
                mt: 'auto',
                p: 2,
                borderTop: '1px solid',
                borderColor: theme.palette.divider,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: '10px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}
              >
                Powered by
              </Typography>
              <Box
                component="img"
                src={IndySoftLogo}
                alt="IndySoft"
                sx={{
                  height: 20,
                  width: 'auto',
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );

  // Only show sidebar on desktop when there's content
  if (!sidebarContent) {
    return null;
  }

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: effectiveWidth },
        flexShrink: { sm: 0 },
        transition: 'width 0.2s'
      }}
    >
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`
          }
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: effectiveWidth,
            transition: 'width 0.2s',
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`
          }
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
